/**
 * Microsoft Advertising Customer Management REST helpers (Story 19.1, Task 2)
 *
 * Helper functions for the Microsoft Advertising Customer Management Service v13
 * REST endpoints used during OAuth post-callback validation:
 *
 *   - GET  /User/Query           — returns the authenticated user + customer roles + account IDs
 *   - POST /Accounts/Search      — enumerates AdvertiserAccount records (name, currency, time zone)
 *
 * Microsoft Advertising requires TWO credentials per request:
 *   1. OAuth Bearer token (per-user, per-tenant)
 *   2. Universal Developer Token (single value per ProvenLeap deployment, never per-user)
 *
 * The Developer Token lives in `MICROSOFT_ADS_DEVELOPER_TOKEN`. It is server-only
 * and is never returned to the browser.
 *
 * REST migration deadline: Oct 1, 2026 (SOAP being phased out). All endpoints
 * here are REST.
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { MicrosoftAdsApiCallError, parseMicrosoftAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const MICROSOFT_ADS_API_BASE =
  "https://clientcenter.api.bingads.microsoft.com/CustomerManagement/v13";

const API_TIMEOUT_MS = 25_000;

// ============================================================================
// Types
// ============================================================================

/**
 * Each role describes a customer the user can access plus the account IDs
 * within that customer.
 */
export interface MicrosoftAdsCustomerRole {
  customerId: string;
  customerName?: string;
  /** Account IDs available within this customer */
  accountIds: string[];
  /**
   * Agency-linked account IDs within this customer (Story 31-12, AC #5).
   * `GetUser` returns both `AccountIds` and `LinkedAccountIds` per role and
   * the user "has the same role on the CustomerId, AccountIds, and
   * LinkedAccountIds for a given CustomerRole" — linked accounts ARE
   * accessible resources, so the catalog must include them.
   *
   * The parse ALWAYS sets this (absent raw array → `[]`, never `undefined`).
   * The field is optional in the TYPE only so pre-31-12 consumers/mocks that
   * construct `MicrosoftAdsUserInfo` literals keep compiling.
   */
  linkedAccountIds?: string[];
  /**
   * `CustomerLinkPermission` from the raw role, when Microsoft returns it
   * (e.g. "Administrative" / "Standard" for agency links). Carried for
   * forward-compat; not currently used for any authorization decision.
   */
  customerLinkPermission?: string;
}

export interface MicrosoftAdsUserInfo {
  userId: string;
  /** UI-friendly user name when Microsoft returns it. */
  userName?: string;
  customerRoles: MicrosoftAdsCustomerRole[];
}

/**
 * Microsoft `AdvertiserAccount` shape (subset we use). The full record has
 * many more fields documented at:
 *   https://learn.microsoft.com/en-us/advertising/customer-management-service/advertiseraccount
 */
export interface MicrosoftAdsAdvertiserAccount {
  customerId: string;
  accountId: string;
  accountName: string;
  currencyCode?: string;
  timeZone?: string;
  accountStatus?: string;
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Microsoft Advertising REST headers.
 *
 * Two auth credentials are mandatory:
 *   - `Authorization: Bearer <access_token>`
 *   - `DeveloperToken: <MICROSOFT_ADS_DEVELOPER_TOKEN>`
 *
 * Without DeveloperToken every call returns 400 (`InternalApiError`/missing
 * developer token), even in the sandbox.
 */
export function buildMicrosoftAdsHeaders(
  accessToken: string,
  developerToken: string
): Record<string, string> {
  if (!accessToken) {
    throw new Error("accessToken is required for Microsoft Ads API calls");
  }
  if (!developerToken) {
    throw new Error(
      "developerToken is required for Microsoft Ads API calls (set MICROSOFT_ADS_DEVELOPER_TOKEN)"
    );
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    DeveloperToken: developerToken,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Response shapes (raw Microsoft envelope)
// ============================================================================

/**
 * Raw shape of GET /User/Query (REST). Microsoft returns the User and an
 * array of CustomerRoles where each role contains its CustomerId and the
 * AccountIds visible to the user.
 */
interface RawUserQueryResponse {
  User?: {
    Id?: number | string;
    UserName?: string;
    Name?: { FirstName?: string; LastName?: string };
  };
  CustomerRoles?: Array<{
    CustomerId?: number | string;
    CustomerName?: string;
    AccountIds?: Array<number | string>;
    LinkedAccountIds?: Array<number | string>;
    CustomerLinkPermission?: string;
  }>;
}

/**
 * Raw shape of POST /Accounts/Search (REST). Returns an `Accounts` array
 * with `AdvertiserAccount` records.
 */
interface RawAccountsSearchResponse {
  Accounts?: Array<{
    Id?: number | string;
    Number?: string;
    Name?: string;
    ParentCustomerId?: number | string;
    CurrencyCode?: string;
    TimeZone?: string;
    AccountLifeCycleStatus?: string;
  }>;
}

// ============================================================================
// User/Query
// ============================================================================

/**
 * Calls GET /User/Query and normalises the response into a stable shape.
 *
 * Returns the authenticated user's id + UI-friendly name + every (customerId,
 * accountIds[]) pair the user can see. Used immediately after token exchange
 * to validate that the OAuth grant actually maps to a Microsoft Advertising
 * customer (a user with no MS Ads customer should not be onboarded).
 */
export async function fetchMicrosoftAdsUserInfo(
  accessToken: string,
  developerToken: string
): Promise<MicrosoftAdsUserInfo> {
  const headers = buildMicrosoftAdsHeaders(accessToken, developerToken);

  const result = await safeFetch(
    `${MICROSOFT_ADS_API_BASE}/User/Query`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildMicrosoftAdsApiError(
      result.error,
      "Microsoft Ads User/Query failed"
    );
  }

  // safeFetch only returns success=true when response.ok, so we can read directly.
  const response = result.data;

  let data: RawUserQueryResponse;
  try {
    data = (await response.json()) as RawUserQueryResponse;
  } catch {
    throw new Error("Invalid Microsoft Ads User/Query response: not JSON");
  }

  const userId = data.User?.Id != null ? String(data.User.Id) : "";
  const first = data.User?.Name?.FirstName?.trim() ?? "";
  const last = data.User?.Name?.LastName?.trim() ?? "";
  const userName =
    data.User?.UserName ||
    [first, last].filter(Boolean).join(" ") ||
    undefined;

  const customerRoles: MicrosoftAdsCustomerRole[] = (
    data.CustomerRoles ?? []
  ).map((role) => ({
    customerId: role.CustomerId != null ? String(role.CustomerId) : "",
    customerName: role.CustomerName,
    accountIds: (role.AccountIds ?? []).map((id) => String(id)),
    // Story 31-12 AC #5: absent raw array coerces to [] (mirror AccountIds),
    // never `undefined` into the discovery loop.
    linkedAccountIds: (role.LinkedAccountIds ?? []).map((id) => String(id)),
    customerLinkPermission: role.CustomerLinkPermission,
  }));

  return {
    userId,
    userName,
    customerRoles,
  };
}

// ============================================================================
// Accounts/Search
// ============================================================================

/**
 * Calls POST /Accounts/Search to enumerate `AdvertiserAccount` records for a
 * given customer + account-id list. Used post-callback to populate the
 * `metadata.allCustomerAccounts` JSON column so 19-2 can surface an account
 * picker without re-running OAuth.
 *
 * Best-effort: callers should treat failure as non-fatal — the OAuth flow
 * still completes off the User/Query result.
 *
 * @param accountIds - The AccountIds returned by User/Query for one customer.
 *   Microsoft requires each AccountIds list be ≤ 1,000; we don't enforce that
 *   here because realistic agency customers stay well below it. If we ever
 *   need to handle larger sets we'll batch externally.
 */
export async function fetchMicrosoftAdsAccountsSearch(
  accessToken: string,
  developerToken: string,
  customerId: string,
  accountIds: string[]
): Promise<MicrosoftAdsAdvertiserAccount[]> {
  if (!customerId) {
    throw new Error("customerId is required for Accounts/Search");
  }
  if (accountIds.length === 0) {
    return [];
  }

  const headers = buildMicrosoftAdsHeaders(accessToken, developerToken);

  // Microsoft Customer Management REST `Accounts/Search` supports the predicate
  // operators Equals / Contains / GreaterThan / GreaterThanEquals / LessThan /
  // LessThanEquals. There is NO `In` operator on this endpoint (Story 19.1
  // review #1). We scope the search by `ParentCustomerId Equals <customerId>`
  // — which returns every AdvertiserAccount under that customer — and then
  // client-side filter to the AccountIds we care about. That way one round
  // trip enumerates the whole multi-account tree without us needing to issue
  // one Equals predicate per account.
  const body = {
    Predicates: [
      {
        Field: "ParentCustomerId",
        Operator: "Equals",
        Value: customerId,
      },
    ],
    // Story 31-12 Task 3.3: SearchAccounts documents PageInfo as a request
    // element; previously we sent none and relied on Microsoft's default
    // page. Pin Index 0 / Size 1000 (the documented per-page max) so large
    // customers return the fullest single page. If a customer ever exceeds
    // 1,000 accounts, truncation here only degrades NAMES at the discovery
    // layer — catalog MEMBERSHIP always comes from User/Query (31-12 AC #6a).
    PageInfo: { Index: 0, Size: 1000 },
  };
  const wantedIds = new Set(accountIds);

  const result = await safeFetch(
    `${MICROSOFT_ADS_API_BASE}/Accounts/Search`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildMicrosoftAdsApiError(
      result.error,
      "Microsoft Ads Accounts/Search failed"
    );
  }

  // safeFetch only returns success=true when response.ok, so we can read directly.
  const response = result.data;

  let data: RawAccountsSearchResponse;
  try {
    data = (await response.json()) as RawAccountsSearchResponse;
  } catch {
    throw new Error(
      "Invalid Microsoft Ads Accounts/Search response: not JSON"
    );
  }

  const allAccounts: MicrosoftAdsAdvertiserAccount[] = (data.Accounts ?? []).map(
    (acct) => ({
      customerId:
        acct.ParentCustomerId != null ? String(acct.ParentCustomerId) : customerId,
      accountId: acct.Id != null ? String(acct.Id) : "",
      accountName: acct.Name ?? "",
      currencyCode: acct.CurrencyCode,
      timeZone: acct.TimeZone,
      accountStatus: acct.AccountLifeCycleStatus,
    })
  );

  // Filter to the AccountIds the caller asked about. In normal operation
  // `User/Query` returned exactly the user's authorised set under this
  // customer, so this filter is a no-op. We keep it as defence-in-depth
  // against `Accounts/Search` returning stale Microsoft side-state (e.g.,
  // newly granted accounts we don't know about yet) so the OAuth callback's
  // composite `account_id` only ever points at IDs the user explicitly
  // surfaced through the OAuth grant. (review #3 L3)
  return allAccounts.filter((a) => wantedIds.has(a.accountId));
}

// ============================================================================
// Internal: build a typed MicrosoftAdsApiCallError from a safeFetch error
// ============================================================================
//
// The adapter relies on `MicrosoftAdsApiCallError.type` (review #2 M3) so it
// can route 401 → token_expired without string-matching localised provider
// messages. This helper centralises the parsing logic shared by all REST calls.

function buildMicrosoftAdsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): MicrosoftAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON; fall back to status-only mapping
    }
  }
  const parsed = parseMicrosoftAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  // Always produce a typed error — even when parsing yielded nothing
  // recognisable we synthesise an "unknown" type so the call site can rely on
  // `instanceof MicrosoftAdsApiCallError`.
  return new MicrosoftAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}
